import React, { useEffect, useState } from "react";
import {
    DataGridPremium,
    GridColDef,
    GridToolbar,
    GridRenderCellParams,
    GridFilterModel,
} from '@mui/x-data-grid-premium';
import { createTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import totalLocationData from "../../../data/locationdata";

const defaultTheme = createTheme();

const useCellStyles = makeStyles(() =>
    createStyles({
        root: {
            alignItems: 'center',
            lineHeight: '24px',
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
            '& .cellValue': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
    }),
);

interface GridCellExpandProps {
    value: string;
    width: number;
}

function isOverflown(element: Element): boolean {
    return (
        element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const { width, value } = props;
    const wrapper = React.useRef<HTMLDivElement | null>(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const classes = useCellStyles();
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = isOverflown(cellValue.current!);
        setShowPopper(isCurrentlyOverflown);
        setAnchorEl(cellDiv.current);
        setShowFullCell(true);
    };

    const handleMouseLeave = () => {
        setShowFullCell(false);
    };

    React.useEffect(() => {
        if (!showFullCell) return undefined;
        function handleKeyDown(nativeEvent: KeyboardEvent) {
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false);
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => { document.removeEventListener('keydown', handleKeyDown); };
    }, [setShowFullCell, showFullCell]);

    return (
        <div
            ref={wrapper}
            className={classes.root}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                ref={cellDiv}
                style={{ height: 1, width, display: 'block', position: 'absolute', top: 0 }}
            />
            <div ref={cellValue} className="cellValue">
                {value}
            </div>
            {showPopper && (
                <Popper
                    open={showFullCell && anchorEl !== null}
                    anchorEl={anchorEl}
                    style={{ width, marginLeft: -17 }}
                >
                    <Paper elevation={1} style={{ minHeight: wrapper.current!.offsetHeight - 3 }}>
                        <Typography variant="body2" style={{ padding: 8, whiteSpace: 'pre-wrap' }}>
                            {value}
                        </Typography>
                    </Paper>
                </Popper>
            )}
        </div>
    );
});

function renderCellExpand(params: GridRenderCellParams<any>) {
    return (
        <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />
    );
}

const ExitSurveyResult: React.FC<any> = (props) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [alignment, setAlignment] = useState("total");
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [
            { id: 1, field: "location", operator: "isAnyOf", value: [] }
        ]
    });

    const columns: GridColDef[] = [
        { field: 'name', headerName: '이름', width: 100, filterable: true },
        { field: 'location', headerName: '지점', width: 100, filterable: true },
        { field: 'stayDuration', headerName: '재원기간', width: 110, filterable: false },
        { field: 'firstCome', headerName: '첫등원일', width: 110, filterable: false },
        { field: 'exitDate', headerName: '퇴원일', width: 110, filterable: false },
        { field: 'regret', headerName: '아쉬운점', width: 650, filterable: false, renderCell: renderCellExpand },
        { field: 'willStay', headerName: '계속 다닐 의향', width: 330, filterable: false },
        { field: 'submissionDate', headerName: '설문 제출일', width: 130, filterable: false },
        { field: 'selectedGift', headerName: '선택한 상품', width: 160, filterable: false },
    ];

    const handleAlignmentChange = (e: any, newAlignment: string) => {
        if (!newAlignment) return;
        setAlignment(newAlignment);

        const locationList: string[] = [];
        if (newAlignment !== "total") {
            const found = totalLocationData.find(l => l.english === newAlignment);
            if (found) locationList.push(found.korean);
        }

        setFilterModel({
            items: [{ id: 1, field: "location", operator: "isAnyOf", value: locationList }]
        });
    };

    useEffect(() => {
        setLoading(true);

        fetch("https://peetsunbae.com/dashboard/avatar/exitsurveydatalist", {
            method: "GET",
            credentials: "include",
        })
            .then((res) => res.json())
            .then((result) => {
                setLoading(false);

                if (result.message !== "success") return;

                const newRows = result.data.map((each: any) => {
                    let questions: any[] = [];
                    if (Array.isArray(each.questions)) {
                        questions = each.questions;
                    } else {
                        try { questions = JSON.parse(each.questions); } catch (e) {}
                    }

                    const row: any = { id: each.id };

                    row.name = each.name;
                    row.location = each.location || "-";

                    if (each.firstCome) {
                        const d = new Date(each.firstCome);
                        row.firstCome = `${d.getMonth() + 1}월 ${d.getDate()}일`;
                    } else {
                        row.firstCome = "-";
                    }

                    if (each.exitDate) {
                        const d = new Date(each.exitDate);
                        row.exitDate = `${d.getMonth() + 1}월 ${d.getDate()}일`;
                    } else {
                        row.exitDate = "-";
                    }

                    if (each.firstCome && each.exitDate) {
                        const start = new Date(each.firstCome);
                        const end = new Date(each.exitDate);
                        let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                        const temp = new Date(start);
                        temp.setMonth(temp.getMonth() + months);
                        if (temp > end) months--;
                        const temp2 = new Date(start);
                        temp2.setMonth(temp2.getMonth() + months);
                        const days = Math.floor((end.getTime() - temp2.getTime()) / (1000 * 60 * 60 * 24));
                        row.stayDuration = `${months}개월 ${days}일`;
                    } else {
                        row.stayDuration = "-";
                    }

                    const submissionDate = new Date(each.submissionDate);
                    row.submissionDate = `${submissionDate.getMonth() + 1}/${submissionDate.getDate()} ${String(submissionDate.getHours()).padStart(2, '0')}:${String(submissionDate.getMinutes()).padStart(2, '0')}`;

                    row.regret = questions.find((q: any) => q.id === "63mE")?.value || "";
                    row.willStay = questions.find((q: any) => q.id === "qjH7")?.value || "";
                    row.selectedGift = questions.find((q: any) => q.id === "6j1v")?.value || "";

                    return row;
                });

                setRows(newRows);
            })
            .catch(() => { setLoading(false); });
    }, []);

    return (
        <div>
            <div style={{ marginTop: "12px", marginBottom: "8px" }}>
                <ToggleButtonGroup
                    color="primary"
                    value={alignment}
                    exclusive
                    onChange={handleAlignmentChange}
                >
                    {props.user && totalLocationData
                        .filter((each: any) => each.academy.includes(props.user.academy))
                        .map((each: any, index: number) => (
                            <ToggleButton value={each.english} key={index}>{each.korean}</ToggleButton>
                        ))
                    }
                </ToggleButtonGroup>
            </div>
            <div style={{ height: 700, width: '100vw', backgroundColor: "white" }}>
                <DataGridPremium
                    filterModel={filterModel}
                    onFilterModelChange={(model) => setFilterModel(model)}
                    loading={loading}
                    density="compact"
                    columns={columns}
                    rows={rows}
                    slots={{ toolbar: GridToolbar }}
                />
            </div>
        </div>
    );
};

export default ExitSurveyResult;
